/**
 * Collapses a sorted list of season numbers into a compact label.
 *
 *   [1,2,3,4,5,6] complete -> "All 6"
 *   [6]                    -> "S6"
 *   [2,3]                  -> "S2-3"
 *   [1,2,3,5]              -> "S1-3, 5"
 *
 * `complete` comes from the API rather than being recomputed here: the back-end
 * knows the show's real season count, which the caller does not.
 */
export function formatSeasons(seasons: number[], complete: boolean): string {
    if (!seasons || seasons.length === 0) return "—";
    if (complete) return `All ${seasons.length}`;

    const sorted = [...seasons].sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = sorted[0];
    let previous = sorted[0];

    for (let i = 1; i <= sorted.length; i++) {
        const current = sorted[i];
        // Close the run when the numbers stop being consecutive, or we run out.
        if (current !== previous + 1) {
            ranges.push(start === previous ? `${start}` : `${start}-${previous}`);
            start = current;
        }
        previous = current;
    }

    return `S${ranges.join(", ")}`;
}

/** Longer form for a tooltip, e.g. "Seasons 1-3, 5 (42 of 60 episodes)". */
export function describeSeasons(
    seasons: number[],
    complete: boolean,
    episodeCount: number,
    totalEpisodes: number
): string {
    if (!seasons || seasons.length === 0) return "No season information";

    const label = formatSeasons(seasons, complete).replace(/^S/, "Seasons ");
    const episodes =
        totalEpisodes > 0
            ? ` (${episodeCount} of ${totalEpisodes} episodes)`
            : ` (${episodeCount} episodes)`;
    return label + episodes;
}
